import React from 'react';
import { 
  createBrowserRouter,
  RouterProvider,
  Route,
  createRoutesFromElements
} from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import UnifiedEvidenceInterface from './components/UnifiedEvidenceInterface';
import CivilClaimsRegistry from './components/CivilClaimsRegistry';
import HowToPage from './pages/HowToPage';
import TestPage from './pages/TestPage';
import CaseRegistrationPage from './pages/CaseRegistrationPage';
import MyClaimsPage from './pages/MyClaimsPage';
import WalletTest from './pages/WalletTest';
import AgreementsPage from './pages/AgreementsPage';
import { WalletProvider } from './utils/walletContext';
import { ClaimProvider } from './utils/claimContext';
import { ThemeProvider } from './contexts/ThemeContext';
import WalletDetector from './components/WalletDetector';

function HomePage() {
  const [activeTab, setActiveTab] = React.useState('form');
  const contentRef = React.useRef(null);

  // Function to handle tab switching with smooth scroll
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Smooth scroll to top of content when switching tabs
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <div className="fixed inset-0 -z-10 grid-pattern opacity-30"></div>
      <div className="fixed top-0 left-0 right-0 h-screen w-screen -z-10 bg-gradient-radial from-primary-900/20 via-transparent to-transparent opacity-30"></div>
      <Navbar />
      <main className="app-main min-h-screen transition-colors duration-300 bg-white dark:bg-gradient-to-br dark:from-dark-900 dark:to-dark-950 text-gray-900 dark:text-white" style={{background: 'var(--background)', color: 'var(--text)'}}>
        <div className="container mx-auto px-4 py-8 pt-24">
          <h1 className="text-4xl font-bold text-center mb-6 bg-gradient-to-r from-primary-600 to-accent bg-clip-text text-transparent">
            Unified Evidence Platform
          </h1>
          <p className="text-center mb-8 text-lg max-w-3xl mx-auto text-gray-600 dark:text-gray-400">
            Complete evidence management: storage, timestamping, and blockchain memorialization
          </p>
          
          {/* Toggle between components using tabs with improved styling */}
          <div className="tabs mb-12 flex justify-center border-b border-gray-200 dark:border-gray-700">
            <button 
              className={`tab px-6 py-3 mx-3 focus:outline-none transition-all duration-300 ease-in-out relative ${
                activeTab === 'form' 
                  ? 'text-primary-600 dark:text-primary-400 font-semibold' 
                  : 'text-gray-500 dark:text-gray-400 font-normal hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              onClick={() => handleTabChange('form')}
            >
              {activeTab === 'form' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-600 to-accent"></div>
              )}
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Evidence Manager
              </span>
            </button>
            <button 
              className={`tab px-6 py-3 mx-3 focus:outline-none transition-all duration-300 ease-in-out relative ${
                activeTab === 'registry' 
                  ? 'text-primary-600 dark:text-primary-400 font-semibold' 
                  : 'text-gray-500 dark:text-gray-400 font-normal hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              onClick={() => handleTabChange('registry')}
            >
              {activeTab === 'registry' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-600 to-accent"></div>
              )}
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                </svg>
                Blockchain Registry
              </span>
            </button>
          </div>
          
          {/* Conditionally render components based on the active tab with fade transition */}
          <div ref={contentRef} className="tab-content-container">
            <div 
              className={`tab-content transition-all duration-500 ease-in-out ${activeTab === 'form' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 hidden'}`}
            >
              <div className="card max-w-6xl mx-auto">
                <UnifiedEvidenceInterface />
              </div>
            </div>
            
            <div 
              className={`tab-content transition-all duration-500 ease-in-out ${activeTab === 'registry' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 hidden'}`}
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                  Smart Contract Registry
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                  Interact directly with the CivilClaimsCaseRegistry contract on Base Mainnet
                </p>
              </div>
              <div className="card max-w-6xl mx-auto">
                <CivilClaimsRegistry />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function Footer() {
  return (
    <footer className="bg-gray-50 dark:bg-dark-900 border-t border-gray-200 dark:border-dark-700 mt-16 transition-colors duration-300">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Unified Evidence Platform - unified.evidencer.io
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-xs mt-2">
            Powered by Base blockchain and IPFS for secure evidence management
          </p>
          <p className="text-gray-400 dark:text-gray-600 text-xs mt-2">
            Contract Address: <a 
              href="https://basescan.org/address/0x4B2cF77Dbd063299536AB916Fb1445bD3267162F"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              0x4B2cF77Dbd063299536AB916Fb1445bD3267162F
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

// Create router with proper future flags
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/" element={<HomePage />} />
      <Route path="/how-to" element={<HowToPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/file-claim" element={<HomePage />} />
      <Route path="/view-cases" element={<MyClaimsPage />} />
      <Route path="/register-case" element={<CaseRegistrationPage />} />
      <Route path="/agreements" element={<AgreementsPage />} />
      <Route path="/test-ipfs-blockchain" element={<TestPage />} />
      <Route path="/wallet-test" element={<WalletTest />} />
      <Route path="*" element={<HomePage />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

function App() {
  return (
    <ThemeProvider>
      <WalletProvider>
        <ClaimProvider>
          <WalletDetector>
            <div className="App">
              <RouterProvider router={router} />
            </div>
          </WalletDetector>
        </ClaimProvider>
      </WalletProvider>
    </ThemeProvider>
  );
}

function AboutPage() {
  return (
    <>
      <div className="fixed inset-0 -z-10 grid-pattern opacity-30"></div>
      <div className="fixed top-0 left-0 right-0 h-screen w-screen -z-10 bg-gradient-radial from-primary-900/20 via-transparent to-transparent opacity-30"></div>
      <Navbar />
      <div className="min-h-screen bg-white dark:bg-dark-950 transition-colors duration-300">
        <div className="container mx-auto px-4 py-8 pt-24">
          <div className="max-w-5xl mx-auto">
          {/* Hero section */}
          <section className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
              About Unified Evidence Platform
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Transforming the civil claims process through blockchain innovation and transparent case management.
            </p>
            <div className="mt-8 inline-block bg-gradient-to-r from-primary-600 to-primary-800 h-1 w-20 rounded-full"></div>
          </section>

          {/* Mission and vision */}
          <section className="mb-16 grid md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-blue-500 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="rounded-full bg-blue-100 p-3 mr-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Our Mission</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                To provide an accessible, efficient, and secure platform for filing and managing civil claims
                cases in California using Web3 technology. We aim to reduce barriers to justice by creating
                a system that is transparent, tamper-proof, and available to all Californians.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-green-500 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Our Vision</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                A judicial system where technology enhances access to justice, where case records are 
                immutable and transparent, and where the civil claims process is streamlined for everyone. 
                We envision a future where legal technology empowers individuals to resolve disputes 
                efficiently and equitably.
              </p>
            </div>
          </section>

          {/* Technology section */}
          <section className="mb-16 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl shadow-md">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Our Technology</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="rounded-full bg-blue-100 p-3 mb-4 inline-block">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Blockchain Integration</h3>
                <p className="text-gray-600">
                  Our platform utilizes the Base blockchain (Ethereum L2) to create immutable records of case filings, 
                  status changes, and judgments, ensuring transparency and preventing record tampering.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="rounded-full bg-indigo-100 p-3 mb-4 inline-block">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Secure IPFS Storage</h3>
                <p className="text-gray-600">
                  All case documents and evidence are stored on IPFS with optional encryption, 
                  providing decentralized and tamper-proof document management while protecting sensitive information.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="rounded-full bg-green-100 p-3 mb-4 inline-block">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Role-Based Access</h3>
                <p className="text-gray-600">
                  Our system implements secure role-based access control, ensuring court officials, plaintiffs, and defendants
                  have appropriate permissions to interact with case records on the blockchain.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Benefits</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-800">Immutable Records</h3>
                  <p className="mt-1 text-gray-600">Case records cannot be altered once created, ensuring an authentic and trustworthy history of all proceedings.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-800">Transparency</h3>
                  <p className="mt-1 text-gray-600">All parties can verify case status and history through blockchain records, promoting fairness and accountability.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-800">Accessibility</h3>
                  <p className="mt-1 text-gray-600">Case information can be accessed remotely at any time, reducing the need for in-person court visits.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-800">Security</h3>
                  <p className="mt-1 text-gray-600">Cryptographic security protects case integrity, while optional encryption safeguards sensitive information.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contract information */}
          <section className="bg-gray-800 text-white p-8 rounded-lg shadow-xl mb-16">
            <h2 className="text-2xl font-bold mb-6">Smart Contract Information</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-blue-300">Civil Claims Case Registry</h3>
                <p className="mb-2 text-gray-300">Primary contract for case registration and management</p>
                <div className="bg-gray-700 p-2 rounded">
                  <div className="text-xs text-gray-400 mb-1">Address:</div>
                  <a 
                    href="https://basescan.org/address/0x4B2cF77Dbd063299536AB916Fb1445bD3267162F" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-200 text-xs font-mono truncate block"
                    style={{ fontSize: '0.75rem', letterSpacing: '-0.01em' }}
                  >
                    0x4B2cF77Dbd063299536AB916Fb1445bD3267162F
                  </a>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  <span>Verified on: </span>
                  <a 
                    href="https://basescan.org/address/0x4B2cF77Dbd063299536AB916Fb1445bD3267162F#code" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-100 underline ml-1"
                  >
                    BaseScan Code
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3 text-green-300">Legal Precedent Oracle</h3>
                <p className="mb-2 text-gray-300">Smart contract for legal precedent verification</p>
                <div className="bg-gray-700 p-2 rounded">
                  <div className="text-xs text-gray-400 mb-1">Address:</div>
                  <a 
                    href="https://basescan.org/address/0x1035C14467a04a081DE75E16CBdce44E7559C5Eb" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-300 hover:text-green-200 text-xs font-mono truncate block"
                    style={{ fontSize: '0.75rem', letterSpacing: '-0.01em' }}
                  >
                    0x1035C14467a04a081DE75E16CBdce44E7559C5Eb
                  </a>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  <span>Also verified on: </span>
                  <a 
                    href="https://repo.sourcify.dev/contracts/full_match/8453/0x1035C14467a04a081DE75E16CBdce44E7559C5Eb/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-gray-100 underline ml-1"
                  >
                    Sourcify
                  </a>
                </div>
              </div>
              
            </div>
          </section>

          {/* Team / Contact */}
          {/* <section className="mb-16 bg-white p-8 rounded-lg shadow-md text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Contact Us</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              We're committed to making the civil claims process more accessible through blockchain technology. 
              For questions, support, or to request a demo, please reach out to our team.
            </p>
            <a 
              href="mailto:support@evidencer.io" 
              className="inline-block bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </a>
          </section> */}

          {/* Disclaimer */}
          <section className="text-center border-t border-gray-200 pt-8">
            <p className="text-sm text-gray-500 max-w-3xl mx-auto">
              This application is for demonstration purposes and is part of the Evidencer ecosystem. 
              While it implements actual blockchain technology, it should not be considered legal advice. 
              Always consult with a qualified legal professional for guidance on civil claims matters.
            </p>
          </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default App;